// Service autonome de la case Operation x BDC (acheteur).
// Cible les vraies routes acheteur /bdc/operation/* (confirmees cote core api.php).
// Remplace les fuites de type : bcService.refuserCommande -> /operation-ms/... (MS)
// et useBCActions.handleAccepterCommande -> /bdc/commande/... (granularite prestataire).

import { Signataire } from '../types';

const authedFetch = async <T = unknown>(path: string, options: RequestInit = {}): Promise<T> => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error("Aucun token d'authentification trouvé");

  const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(options.headers || {}),
    },
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { message?: string };
    throw new Error(data.message || `Erreur HTTP: ${response.status}`);
  }
  return response.json().catch(() => ({})) as Promise<T>;
};

// Reponses API typees.
export interface ReferenceUpdateResponse {
  data?: unknown;
}
export interface NotifierResponse {
  responsable?: { prenom?: string; nom?: string };
}

// Acheteur accepte l'offre d'un rang : POST /bdc/operation/{id}/accept/{commandeId}
export const accepterOffre = (operationId: number, commandeId: number): Promise<unknown> =>
  authedFetch(`/bdc/operation/${operationId}/accept/${commandeId}`, { method: 'POST' });

// Acheteur refuse l'offre d'un rang (cascade) : POST /bdc/operation/{id}/refuse/{commandeId}
export const refuserOffre = (operationId: number, commandeId: number): Promise<unknown> =>
  authedFetch(`/bdc/operation/${operationId}/refuse/${commandeId}`, { method: 'POST' });

// Acheteur annule l'operation (cascade -> statut annule) : POST /bdc/operation/{id}/cancel
export const annulerOperation = (operationId: number): Promise<unknown> =>
  authedFetch(`/bdc/operation/${operationId}/cancel`, { method: 'POST' });

// Met a jour le numero de reference interne : PUT /bdc/operation/{id}
export const updateReferenceInterne = (
  operationId: number,
  reference: string,
): Promise<ReferenceUpdateResponse> =>
  authedFetch<ReferenceUpdateResponse>(`/bdc/operation/${operationId}`, {
    method: 'PUT',
    body: JSON.stringify({ reference }),
  });

// Liste des acheteurs signataires (validateurs) : GET /acheteurs/signataires
export const loadSignataires = async (): Promise<Signataire[]> => {
  const result = await authedFetch<{ data?: Signataire[]; signataires?: Signataire[] }>(
    '/acheteurs/signataires',
  );
  return result.data || result.signataires || [];
};

// Notifie un responsable signataire pour validation : POST /bdc/operation/{id}/notification-responsable
export const notifierResponsable = (
  operationId: number,
  responsableId: number,
  responsableType: string,
): Promise<NotifierResponse> =>
  authedFetch<NotifierResponse>(`/bdc/operation/${operationId}/notification-responsable`, {
    method: 'POST',
    body: JSON.stringify({ responsable_id: responsableId, responsable_type: responsableType }),
  });

// Telecharge le bon de commande signe (reponse PDF brute) : POST /documents/signed-contrat
export const telechargerBonCommande = async (externalId: string | number): Promise<Blob> => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error("Aucun token d'authentification trouvé");

  const response = await fetch(`${process.env.REACT_APP_API_URL}/documents/signed-contrat`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/pdf',
    },
    body: JSON.stringify({ external_id: String(externalId), external_type: 'operation', source: 'BC' }),
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { message?: string };
    throw new Error(data.message || 'Erreur lors du telechargement du bon de commande');
  }
  return response.blob();
};

// Mode de signature BDC (automatique | manuscrite) : GET /app/signature-mode (public, sans auth)
export const fetchSignatureModeBdc = async (): Promise<'automatique' | 'manuscrite' | null> => {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/app/signature-mode`);
  const data = (await response.json()) as { bdc_mode?: string; mode?: string };
  const mode = data.bdc_mode ?? data.mode;
  return mode === 'manuscrite' || mode === 'automatique' ? mode : null;
};

// --- Onglet Consultation ------------------------------------------------------

// Erreur de validation 422 portant les erreurs par champ (cote API), pour permettre
// au composant de les remapper vers les champs du formulaire.
export class ConsultationValidationError extends Error {
  validationErrors: Record<string, string[]>;

  constructor(validationErrors: Record<string, string[]>) {
    super('Erreurs de validation détectées');
    this.name = 'ConsultationValidationError';
    this.validationErrors = validationErrors;
  }
}

export interface SaveDescriptifResponse {
  status?: string;
  message?: string;
  data?: unknown;
}

// Sauvegarde le descriptif (admin + technique + dates) : PUT /bdc/operation/{id}.
// Remonte les erreurs 422 via ConsultationValidationError pour le mapping de champs.
export const saveOperationDescriptif = async (
  operationId: number | string,
  body: Record<string, unknown>,
): Promise<SaveDescriptifResponse> => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error("Aucun token d'authentification trouvé");

  const response = await fetch(`${process.env.REACT_APP_API_URL}/bdc/operation/${operationId}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as {
      message?: string;
      errors?: Record<string, string[]>;
    };
    if (response.status === 422 && errorData.errors) {
      throw new ConsultationValidationError(errorData.errors);
    }
    throw new Error(errorData.message || `Erreur HTTP ${response.status}`);
  }

  const data = (await response.json()) as SaveDescriptifResponse;
  if (data.status !== 'success') {
    throw new Error(data.message || 'Erreur lors de la sauvegarde');
  }
  return data;
};

// Met a jour la quantite d'un article du catalogue : PUT /bdc/operation/{id}/catalogue/{catalogueId}
export const updateArticleQuantity = (
  operationId: number,
  catalogueId: string | number,
  quantity: number,
): Promise<unknown> =>
  authedFetch(`/bdc/operation/${operationId}/catalogue/${catalogueId}`, {
    method: 'PUT',
    body: JSON.stringify({ quantity: String(quantity) }),
  });

// Supprime un article du catalogue : DELETE /bdc/operation/{id}/catalogue/{catalogueId}
export const removeArticle = (
  operationId: number,
  catalogueId: string | number,
): Promise<unknown> =>
  authedFetch(`/bdc/operation/${operationId}/catalogue/${catalogueId}`, { method: 'DELETE' });

// Le contrat du titulaire vit dans `services/contratTitulaireService` (couche partagee) : les deux
// faces du BC le consomment, acheteur comme prestataire.

// Vide TOUT le catalogue de l'operation : DELETE /bdc/operation/{id}/catalogue
// Un seul appel plutot qu'un par article : la purge est atomique cote serveur, et N suppressions
// concurrentes laisseraient l'operation a moitie videe si l'une echouait.
export const removeAllArticles = (operationId: number): Promise<unknown> =>
  authedFetch(`/bdc/operation/${operationId}/catalogue`, { method: 'DELETE' });

// Transfere l'operation au prestataire (panier inverse) : POST /bdc/operation/{id}/transfertPrestataire
export const transfertPrestataire = (operationId: number): Promise<{ data?: unknown }> =>
  authedFetch<{ data?: unknown }>(`/bdc/operation/${operationId}/transfertPrestataire`, {
    method: 'POST',
  });

// Lance / relance la consultation d'un rang (panier normal) : PUT /bdc/operation/{id}/consultation
export const lancerConsultationRang = (operationId: number): Promise<unknown> =>
  authedFetch(`/bdc/operation/${operationId}/consultation`, { method: 'PUT' });
